<template>
  <div class="asp-page">
    <LoadingBar v-if="loading" message="Loading Profile..." />
    <div v-else-if="loadError" class="asp-status asp-status--error">{{ loadError }}</div>

    <template v-else-if="profile">
      <div class="asp-topbar">
        <div class="asp-topbar__identity">
      <div class="asp-topbar__avatar">
        <img v-if="profile.logoUrl" :src="getFileUrl(profile.logoUrl)" alt="" />
        <span v-else>{{ initials }}</span>
      </div>

      <div>
        <div class="asp-topbar__name-row">
          <h1 class="asp-topbar__name">{{ profile.company_name }}</h1>
          <span class="asp-status-badge" :class="approvalClass(profile.approval_status)">
            {{ titleCaseFromSlug(profile.approval_status) }}
          </span>
        </div>

        <p class="asp-topbar__meta">
          ID {{ profile.external_id }} · {{ profile.email }} · Registered {{ profile.memberSince }}
        </p>

        <p class="asp-topbar__submission" v-if="profile.approval_submitted">
          <i class="fa fa-paper-plane"></i>
          Submitted for approval on {{ formatDate(profile.submitted_at) }}
        </p>

        <p class="asp-topbar__submission asp-topbar__submission--pending" v-else>
          <i class="fa fa-clock"></i>
          Not yet submitted for approval
        </p>
      </div>
    </div>
      <button type="button" class="asp-back" @click="$router.back()"> <i class="fa fa-arrow-left"></i> Back
            </button>
    </div>
      <div class="asp-body">
        <!-- ============ Horizontal tabs ============ -->
        <nav class="asp-tabs">
          <button v-for="tab in tabs" :key="tab.key" type="button" class="asp-tab"
            :class="{ 'is-active': activeTab === tab.key }" @click="activeTab = tab.key">
            <span class="asp-tab__icon">{{ tab.icon }}</span>
            <span>{{ tab.label }}</span>
            <span v-if="tab.count !== undefined" class="asp-tab__count">{{ tab.count }}</span>
          </button>
        </nav>

        <!-- ============ Content ============ -->
        <main class="asp-content">
          <!-- ===== Overview ===== -->
          <section v-show="activeTab === 'overview'" class="asp-panel">
            <div class="asp-stats">
              <div class="asp-stat"><strong>{{ profile.materials.length }}</strong><span>Materials</span></div>
              <div class="asp-stat"><strong>{{ profile.services.length }}</strong><span>Services</span></div>
              <div class="asp-stat"><strong>{{ profile.certificates.length }}</strong><span>Certificates</span></div>
              <div class="asp-stat"><strong>{{ complianceYesCount }}/{{ profile.compliance.length
                  }}</strong><span>Compliance Met</span></div>
              <div class="asp-stat"><strong>{{ profile.businessTypes.length }}</strong><span>Business Types</span></div>
            </div>

            <div class="asp-card">
              <h4 class="asp-card__title">Business Types</h4>
              <div class="asp-chip-row">
                <span v-for="t in profile.businessTypes" :key="t.id" class="asp-type-chip">
                  {{ typeLabel(t.type) }}
                  <span class="asp-status-badge asp-status-badge--sm" :class="approvalClass(t.approval_status)">
                    {{ titleCaseFromSlug(t.approval_status) }}
                  </span>
                </span>
              </div>
            </div>

            <div class="asp-grid-2">
              <div class="asp-card">
                <h4 class="asp-card__title">Company Details</h4>
                <div class="asp-kv"><span>Company Name</span><strong>{{ profile.company_name }}</strong></div>
                <div class="asp-kv"><span>Email</span><strong>{{ profile.email }}</strong></div>
                <div class="asp-kv"><span>Phone</span><strong>{{ profile.phone }}</strong></div>
                <div class="asp-kv"><span>Address</span><strong>{{ profile.physical_address }}</strong></div>
                <div class="asp-kv"><span>City</span><strong>{{ profile.street_city }}</strong></div>
                <div class="asp-kv"><span>State</span><strong>{{ profile.state }}</strong></div>
                <div class="asp-kv"><span>Postal Code</span><strong>{{ profile.postal_code }}</strong></div>
              </div>
              <div class="asp-card">
                <h4 class="asp-card__title">Primary Point of Contact</h4>
                <div class="asp-kv"><span>POC Name</span><strong>{{ profile.poc }}</strong></div>
                <div class="asp-kv"><span>Title</span><strong>{{ profile.title }}</strong></div>
                <div class="asp-kv"><span>Emergency Contact</span><strong>{{ profile.emergency_contact }}</strong></div>

                <h4 class="asp-card__title">QA Contact</h4>
                <div class="asp-kv"><span>QA Name</span><strong>{{ profile.qa_name }}</strong></div>
                <div class="asp-kv"><span>QA Title</span><strong>{{ profile.qa_title }}</strong></div>
                <div class="asp-kv"><span>QA Email</span><strong>{{ profile.qa_email }}</strong></div>
                <div class="asp-kv"><span>QA Phone</span><strong>{{ profile.qa_phone }}</strong></div>
              </div>

            </div>

            <div v-if="profile.businessTypes.some(t => t.type === 'manufacturer')" class="asp-card">
              <h4 class="asp-card__title">Manufacturing & Production</h4>
              <div class="asp-grid">
                <div>
                  <div class="asp-kv"><span>Manufacturing Site</span><strong>{{ ucfirst(profile.manufacturing_site) ||
                      '—' }}</strong></div>
                  <div class="asp-kv" v-if="profile.manufacturing_type"><span>Manufacturing Type</span><strong>{{
                    ucfirst(profile.manufacturing_type) || '—' }}</strong></div>
                  <div class="asp-kv" v-if="profile.product_brand_type"><span>Manufacturer Category</span><strong>{{
                    manufacturerCategoryLabel(profile.product_brand_type) || '—' }}</strong></div>

                  <div class="asp-kv"><span>Production City</span><strong>{{ profile.production_city || '—' }}</strong>
                  </div>
                  <div class="asp-kv"><span>Production State</span><strong>{{ profile.production_state || '—'
                      }}</strong></div>
                  <div class="asp-kv"><span>Production Address</span><strong>{{ profile.production_address || '—'
                      }}</strong></div>
                  <div class="asp-kv"><span>Production Postcode</span><strong>{{ profile.production_postcode || '—'
                      }}</strong></div>
                </div>
              </div>
            </div>

            <div v-if="profile.comments" class="asp-card">
              <h4 class="asp-card__title">Additional Notes / Comments</h4>
              <p class="asp-note">{{ profile.comments }}</p>
            </div>
          </section>

          <!-- ===== Offerings: Materials + Services ===== -->
          <section v-show="activeTab === 'offerings'" class="asp-panel">
            <div class="asp-card">
              <h4 class="asp-card__title">Materials</h4>
              <table v-if="profile.materials.length" class="asp-table">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Packaging</th>
                    <th>Lead Time</th>
                    <th>Sub Ingredients</th>
                    <th>Allergens</th>
                    <th>Documents</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(m, i) in profile.materials" :key="m.id">
                    <td class="asp-td-muted">{{ i + 1 }}</td>
                    <td class="asp-td-strong">{{ m.name }}</td>
                    <td class="asp-td-muted">{{ m.type }}</td>
                    <td class="asp-td-muted">{{ m.packaging_size }} · {{ m.packaging_type }}</td>
                    <td class="asp-td-muted">{{ m.lead_time }}</td>
                    <td class="asp-td-muted">{{ m.sub_ingredients || '—' }}</td>
                    <td class="asp-td-muted">{{ m.allergens }}</td>
                    <td class="asp-td-documents">
                      <div v-if="m.documents && m.documents.length" class="document-list">
                        <a v-for="(doc, idx) in m.documents" 
                          :key="idx" 
                          :href="getFileUrl(doc.url)" 
                          target="_blank" 
                          class="document-link"
                          :title="doc.name">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10 9 9 9 8 9"/>
                          </svg>
                          {{ doc.name }}
                        </a>
                      </div>
                      <span v-else class="asp-td-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="asp-empty">No materials listed.</p>
            </div>

            <div class="asp-card">
              <h4 class="asp-card__title">Services Offered</h4>
              <table v-if="profile.services.length" class="asp-table">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(s, i) in profile.services" :key="s.id">
                    <td class="asp-td-muted">{{ i + 1 }}</td>
                    <td class="asp-td-strong">{{ s.name }}</td>
                    <td class="asp-td-muted">{{ s.service_type }}</td>
                    <td class="asp-td-muted">{{ s.description }}</td>
                    <td><span class="asp-status-badge"
                        :class="s.status === 'Active' ? 'asp-status-badge--approved' : 'asp-status-badge--rejected'">{{
                        s.status }}</span></td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="asp-empty">No services listed.</p>
            </div>
            <div class="asp-card">
              <h4 class="asp-card__title">Products</h4>

              <table v-if="profile.products.length" class="asp-table">
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Production Type</th>
                    <th>Unit</th>
                    <th>SKU</th>
                    <th>Application</th>
                    <th>Description</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(p, i) in profile.products" :key="p.id">
                    <td class="asp-td-muted">{{ i + 1 }}</td>
                    <td class="asp-td-strong">{{ p.name }}</td>
                    <td class="asp-td-muted">{{ p.type }}</td>
                    <td class="asp-td-muted">{{ p.production_type }}</td>
                    <td class="asp-td-muted">{{ p.unit }}</td>
                    <td class="asp-td-muted">{{ p.sku || '—' }}</td>
                    <td class="asp-td-muted">{{ p.application || '—' }}</td>
                    <td class="asp-td-muted">{{ p.description || '—' }}</td>
                  </tr>
                </tbody>
              </table>

              <p v-else class="asp-empty">No products listed.</p>
            </div>
             <div class="asp-card">
              <h4 class="asp-card__title">Regulatory Compliance Questionnaire</h4>
              <table v-if="profile.compliance.length" class="asp-table">
                <thead>
                  <tr>
                    <th class="asp-col-num">#</th>
                    <th>Question</th>
                    <th class="asp-col-answer">Answer</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(q, i) in profile.compliance" :key="q.key">
                    <td class="asp-col-num">{{ i + 1 }}</td>
                    <td>{{ q.text }}</td>
                    <td class="asp-col-answer">
                      <span class="asp-yn" :class="ynClass(q.answer)">{{ ynIcon(q.answer) }} {{ q.answer }}</span>
                    </td>
                    <td class="asp-td-muted  asp-cell-wrap">{{ q.remarks || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="asp-empty">No regulatory responses on file.</p>
            </div>

            <div class="asp-card">
              <h4 class="asp-card__title">Non-Certified Food Safety & QA</h4>
              <table v-if="profile.nonCert.length" class="asp-table">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th class="asp-col-answer">Answer</th>
                    <th>Details</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="q in profile.nonCert" :key="q.key">
                    <td>{{ q.text }}</td>
                    <td class="asp-col-answer">
                      <span class="asp-yn" :class="ynClass(q.answer)">{{ ynIcon(q.answer) }} {{ q.answer }}</span>
                    </td>
                    <td class="asp-details">
                      <template v-if="q.details && typeof q.details === 'object'">
                        <div v-for="(value, key) in q.details" :key="key" class="asp-detail-item">
                          <strong>{{ key }}:</strong> <span>{{ value }}</span>
                        </div>
                      </template>
                      <template v-else-if="q.details">{{ q.details }}</template>
                      <template v-else>—</template>
                    </td>
                    <td class="asp-td-muted  asp-cell-wrap">{{ q.remarks || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="asp-empty">No non-cert responses on file.</p>
            </div>
          </section>

          <!-- ===== Certificates (Food Safety + IP certs together) ===== -->
          <section v-show="activeTab === 'certificates'" class="asp-panel">
            <div class="asp-card">
              <div class="asp-card__title-row">
                <h4 class="asp-card__title">Food Safety Certificates</h4>
                <span class="asp-status-badge"
                  :class="profile.foodSafetyCert === 'yes' ? 'asp-status-badge--approved' : 'asp-status-badge--pending'">
                  {{ profile.foodSafetyCert === 'yes' ? 'Certified' : 'Non-Certified' }}
                </span>
              </div>
              <table v-if="profile.certificates.length" class="asp-table">
                <thead>
                  <tr>
                    <th>Certificate</th>
                    <th>Expiry</th>
                    <th>Comment</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cert in profile.certificates" :key="cert.id">
                    <td class="asp-td-strong">{{ cert.title }}</td>
                    <td class="asp-td-muted">{{ cert.expiryDate || '—' }}</td>
                    <td class="asp-td-muted">{{ cert.comment || '—' }}</td>
                    <td>
                      <a v-if="cert.fileName" :href="getFileUrl(cert.file)" class="asp-link" target="_blank">{{
                        cert.fileName
                        }}</a>
                      <span v-else class="asp-td-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="asp-empty">No certificates uploaded.</p>
            </div>

            <div class="asp-card">
              <div class="asp-card__title-row">
                <h4 class="asp-card__title">Identity Preserved Certifications</h4>
                <span class="asp-status-badge"
                  :class="profile.ipCertOption === 'yes' ? 'asp-status-badge--approved' : 'asp-status-badge--pending'">
                  {{ profile.ipCertOption === 'yes' ? 'On File' : 'Not Provided' }}
                </span>
              </div>
              <table v-if="profile.ipCerts.length" class="asp-table">
                <thead>
                  <tr>
                    <th>Certification</th>
                    <th>Expiry</th>
                    <th>Comment</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cert in profile.ipCerts" :key="cert.id">
                    <td class="asp-td-strong">{{ cert.title }}</td>
                    <td class="asp-td-muted">{{ cert.expiryDate || '—' }}</td>
                    <td class="asp-td-muted">{{ cert.comment || '—' }}</td>
                    <td>
                      <a v-if="cert.fileName" :href="getFileUrl(cert.file)" class="asp-link" target="_blank">{{
                        cert.fileName
                        }}</a>
                      <span v-else class="asp-td-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="asp-empty">No IP certification documents uploaded.</p>
            </div>
          </section>

          <!-- ===== Types & Companies ===== -->
          <section v-show="activeTab === 'types'" class="asp-panel">
            <div class="asp-card">
              <h4 class="asp-card__title">Business Types & Approval History</h4>
              <table class="asp-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Approved At</th>
                    <th>Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in profile.businessTypesRaw" :key="t.id">
                    <td class="asp-td-strong">{{ typeLabel(t.name) }}</td>
                    <td><span class="asp-status-badge" :class="approvalClass(t.approval_status)">{{
                      titleCaseFromSlug(t.approval_status) }}</span></td>
                    <td class="asp-td-muted">{{ t.approved_at || '—' }}</td>
                    <td class="asp-td-muted asp-cell-wrap">{{ t.description || '—' }}</td>
                    <td>
                      <button type="button" v-if="profile.approval_submitted" class="rform__btn rform__btn--sm" @click="openReviewModal(profile)">
                        Review
                      </button>
                       <a v-else class="dropdown-item disabled" style="cursor: default; opacity: 0.6;">
                          <i class="fa fa-clock-o text-muted"></i>
                          <span>Waiting for submission</span>
                        </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
            <section v-show="activeTab === 'reviews'" class="asp-panel">
            <div class="asp-card">
              <!-- Accordion for Connections -->
              <div class="connections-accordion">
                <div class="accordion-item">
                  <button 
                    class="accordion-header" 
                    @click="toggleConnectionsAccordion"
                    :aria-expanded="showConnections"
                  >
                    <div class="accordion-header__content">
                      <span class="accordion-header__icon">🔗</span>
                      <span class="accordion-header__title">Connections Overview</span>
                      <span class="accordion-header__badge">{{ profile?.connections?.total || 0 }}</span>
                    </div>
                    <span class="accordion-header__arrow" :class="{ 'is-open': showConnections }">
                      ▼
                    </span>
                  </button>
                  <div class="accordion-body" v-show="showConnections">
                    <div class="connections-content">
                      <!-- Stats Summary -->
                      <div class="connections-stats-grid">
                        <div class="stat-card">
                          <span class="stat-card__number">{{ profile?.connections?.incoming_count || 0 }}</span>
                          <span class="stat-card__label">Added by Others</span>
                        </div>
                        <div class="stat-card">
                          <span class="stat-card__number">{{ profile?.connections?.outgoing_count || 0 }}</span>
                          <span class="stat-card__label">Added by You</span>
                        </div>
                        <div class="stat-card stat-card--total">
                          <span class="stat-card__number">{{ profile?.connections?.total || 0 }}</span>
                          <span class="stat-card__label">Total Connections</span>
                        </div>
                      </div>

                      <!-- Incoming Connections List -->
                      <div v-if="profile?.connections?.incoming?.length > 0" class="connection-list-section">
                        <h5 class="connection-list-section__title">
                          ⬇️ Companies that added {{ profile?.company_name || 'this supplier' }}
                          <span class="connection-list-section__badge">{{ profile.connections.incoming.length }}</span>
                        </h5>
                        <div class="connection-list">
                          <div 
                            v-for="conn in profile.connections.incoming" 
                            :key="conn.id" 
                            class="connection-item"
                          >
                          <div class="asp-topbar__avatar" v-if="conn.photo">
                              <img :src="getFileUrl(conn.photo)" />
                              </div>
                            <div class="connection-item__avatar" v-else>
                          
                              <span>{{ getInitialsFromName(conn.company_name) }}</span>
                            </div>
                            <div class="connection-item__info">
                              <span class="connection-item__name">{{ conn.company_name }}</span>
                              <span class="connection-item__date">
                                Connected {{ formatDate(conn.connected_at) }}
                              </span>
                            </div>
                            <span class="connection-item__status connection-item__status--incoming">
                              Incoming
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Outgoing Connections List -->
                      <div v-if="profile?.connections?.outgoing?.length > 0" class="connection-list-section">
                        <h5 class="connection-list-section__title">
                          ⬆️ Companies added by {{ profile?.company_name || 'this supplier' }}
                          <span class="connection-list-section__badge">{{ profile.connections.outgoing.length }}</span>
                        </h5>
                        <div class="connection-list">
                          <div 
                            v-for="conn in profile.connections.outgoing" 
                            :key="conn.id" 
                            class="connection-item"
                          >
                            <div class="connection-item__avatar">
                              <img :src="getFileUrl(conn.photo)" v-if="conn.photo"/>
                              <span v-else>{{ getInitialsFromName(conn.company_name) }}</span>
                            </div>
                            <div class="connection-item__info">
                              <span class="connection-item__name">{{ conn.company_name }}</span>
                              <span class="connection-item__date">
                                Connected {{ formatDate(conn.connected_at) }}
                              </span>
                            </div>
                            <span class="connection-item__status connection-item__status--outgoing">
                              Outgoing
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Empty State -->
                      <div v-if="!profile?.connections?.incoming?.length && !profile?.connections?.outgoing?.length" class="connections-empty">
                        <span class="connections-empty__icon">🔗</span>
                        <p class="connections-empty__text">No connections yet</p>
                        <p class="connections-empty__sub"> Not connected to any network</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Reviews Header -->
              <div class="reviews-header">
                <h4 class="asp-card__title">Reviews</h4>
                <div class="reviews-stats">
                  <span class="reviews-stats__average">
                    <span class="reviews-stats__stars">
                      <span v-for="n in 5" :key="n" class="star" :class="{ 'star--filled': n <= Math.round(averageRating) }">★</span>
                    </span>
                    <span class="reviews-stats__number">{{ averageRating }}</span>
                  </span>
                  <span class="reviews-stats__count">{{ reviews.length }} review{{ reviews.length !== 1 ? 's' : '' }}</span>
                </div>
              </div>

              <!-- Rating Distribution -->
              <div v-if="reviews.length > 0" class="rating-distribution">
                <div v-for="star in [5,4,3,2,1]" :key="star" class="rating-bar">
                  <span class="rating-bar__label">{{ star }} ★</span>
                  <div class="rating-bar__track">
                    <div 
                      class="rating-bar__fill" 
                      :style="{ width: `${(getStarDistribution()[star] / reviews.length * 100) || 0}%` }"
                    ></div>
                  </div>
                  <span class="rating-bar__count">{{ getStarDistribution()[star] }}</span>
                </div>
              </div>

              <!-- Reviews List -->
              <div v-if="reviews.length > 0" class="reviews-list">
                <div 
                  v-for="review in reviews" 
                  :key="review.id" 
                  class="review-item"
                >
                  <div class="review-item__header">
                    <div class="review-item__identity">
                      <div class="review-item__avatar">
                        <img v-if="review.reviewer?.photo" :src="getFileUrl(review.reviewer.photo)" alt="" />
                        <span v-else>{{ getInitials(review.reviewer) }}</span>
                      </div>
                      <div>
                        <strong class="review-item__name">{{ getReviewerName(review.reviewer) }}</strong>
                        <span class="review-item__date">{{ formatDate(review.created_at) }}</span>
                      </div>
                    </div>
                    <div class="review-item__stars">
                      <span 
                        v-for="n in 5" 
                        :key="n" 
                        class="star"
                        :class="{ 'star--filled': n <= review.stars }"
                      >
                        ★
                      </span>
                    </div>
                  </div>
                  
                  <p v-if="review.review" class="review-item__content">{{ review.review }}</p>
                  <p v-if="review.comments" class="review-item__comments">{{ review.comments }}</p>
                  
                  <div class="review-item__footer">
                    <span 
                      class="review-item__status" 
                      :class="{
                        'review-item__status--approved': review.status === 'approved',
                        'review-item__status--pending': review.status === 'pending',
                        'review-item__status--rejected': review.status === 'rejected'
                      }"
                    >
                      {{ review.status || 'Pending' }}
                    </span>
                    <span class="review-item__id">ID: {{ review.id }}</span>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="reviews-empty">
                <span class="reviews-empty__icon">📝</span>
                <p class="reviews-empty__text">No reviews yet</p>
                <p class="reviews-empty__sub">They have not received any review yet.</p>
              </div>
            </div>
          </section>
        </main>
      </div>
    </template>
  </div>
  
    <FormModal ref="reviewModal" title="Review Types" id="review_supplier_types_modal">
      <div v-if="reviewingSupplier">
        <p class="rform__help" style="margin-bottom: 14px;">
          Review <strong>{{ reviewingSupplier.company_name }}</strong>'s submitted business types one at a time.
        </p>
 
        <div class="rform__field">
          <label class="rform__label">Business Type</label>
          <div class="rform__select-wrap">
            <select v-model="selectedTypeId" class="rform__select" @change="resetDraft">
              <option v-for="t in reviewingSupplier.supplier_types" :key="t.id" :value="t.id">
                {{ formatBusinessType(t.name) }}
              </option>
            </select>
          </div>
        </div>
 
        <div v-if="selectedType" class="review-current-status">
          <span class="review-current-status__label">Current status:</span>
          <span v-html="getStatusBadge(selectedType.approval_status)" class="mt-3"></span>
        </div>
 
        <div class="rform__field">
          <label class="rform__label rform__label--required">New Status</label>
          <div class="rform__select-wrap">
            <select v-model="statusDraft.status" class="rform__select">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="partially_approved">Partially Approved</option>
            </select>
          </div>
        </div>
 
        <div v-if="statusDraft.status !== 'approved'" class="rform__field">
          <label class="rform__label rform__label--required">Remarks</label>
          <textarea
            v-model="statusDraft.remarks"
            class="rform__textarea"
            rows="3"
            placeholder="Explain the reason for this status..."
            :class="{ 'is-invalid': remarksError }"
          ></textarea>
          <span v-if="remarksError" class="rform__error">{{ remarksError }}</span>
        </div>
 
        <div class="rform__row" style="justify-content: flex-end; margin-top: 16px;">
          <button type="button" class="rform__btn rform__btn--ghost" @click="closeReviewModal('reviewModal')">Close</button>
          <button type="button" class="rform__btn rform__btn--primary" :disabled="saving" @click="saveTypeStatus">
            {{ saving ? 'Saving…' : 'Save Status' }}
          </button>
        </div>
      </div>
    </FormModal>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import FormModal from '@/components/utils/FormModal.vue'
import { getStatusBadge } from '@/utils/getStatusBadge'
export default {
  name: 'AdminSupplierProfile',
  components: { LoadingBar,
    FormModal
   },
  data() {
    return {
      loading: true,
      loadError: '',
      profile: null,
      activeTab: 'overview',
        reviewingSupplier: null,
      savingTypeId: null,
      selectedTypeId: null, 
      statusDraft: { status: 'pending', remarks: '' },
      remarksError: '',
       showConnections: false,
      saving: false,
      reviewStats: {
      total: 0,
      average: 0,
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
     

    }
    }
  },
  computed: {
    tabs() {
      if (!this.profile) return []
      return [
        { key: 'overview', label: 'Overview', icon: '🏢' },
        { key: 'offerings', label: 'Materials ,Products & Services', icon: '📦', count: this.profile.materials.length + this.profile.services.length + this.profile.products.length },
        { key: 'certificates', label: 'Certificates', icon: '🛡️', count: this.profile.certificates.length + this.profile.ipCerts.length },
        { key: 'types', label: 'Approval Status', icon: '🏷️', count: this.profile.businessTypesRaw.length },
          { key: 'reviews', label: 'Reviews & Connections', icon: '⭐', count: this.reviews.length },
      ]
    },
      reviews() {
    return this.profile?.reviews || []
  },
  averageRating() {
    if (!this.reviews.length) return 0
    const total = this.reviews.reduce((sum, r) => sum + r.stars, 0)
    return (total / this.reviews.length).toFixed(1)
  },
    initials() {
      if (!this.profile) return ''
      return (this.profile.company_name || '').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    },
    
    complianceYesCount() {
      if (!this.profile) return 0
      return this.profile.compliance.filter((q) => q.answer === 'YES').length
    },
  selectedType() {
    if (!this.reviewingSupplier || !this.selectedTypeId) return null
    return (this.reviewingSupplier.supplier_types || []).find((t) => t.id === this.selectedTypeId) || null
  },
},
  mounted() {
    this.loadProfile()
  },
  methods: {
    getStatusBadge,
    async loadProfile() {
      this.loading = true
      this.loadError = ''

      const supplierId = this.$route.params.id

      const result = await form_api_submit({
        url: `supplier/profile/${supplierId}`,
        method: 'GET',
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success) {
        this.profile = this.mapProfile(result.data)
        
    await this.loadReviews(this.profile.id)
      } else if (result?.data?.error_type === 'not_found') {
        this.loadError = 'This profile could not be found.'
      } else {
        this.loadError = 'Could not load this profile. Please try again.'
      }

      this.loading = false
    },

      mapProfile(data) {
  const supplier = data.supplier || {}
  const supplierTypesRaw = data.supplier_types || []
  const reviews = (data.reviews || []).map((r) => ({
    id: r.id,
    stars: r.stars,
    review: r.review,
    comments: r.comments,
    status: r.status,
    created_at: r.created_at,
    reviewer: r.reviewer ? {
      id: r.reviewer.id,
      first_name: r.reviewer.first_name,
      last_name: r.reviewer.last_name,
      company_name: r.reviewer.company_name,
      photo: r.reviewer.photo,
    } : null,
    reviewed_by: r.reviewed_by,
  }))
      
      // Map connections data
      const connections = data.connections || {
        incoming: [],
        outgoing: [],
        incoming_count: 0,
        outgoing_count: 0,
        total: 0
      }
      
      return {
        id: supplier.id,  
        approval_submitted: supplier.approval_submitted || false,
        submitted_at: supplier.submitted_at || '',
        supplier_types: supplierTypesRaw, 
        businessTypesRaw: supplierTypesRaw,
        businessTypes: supplierTypesRaw.map((t) => ({
          id: t.id,
          type: t.name,
          approval_status: t.approval_status,
        })),
        
        // Add connection data with lists
        connections: {
          incoming: connections.incoming || [],
          outgoing: connections.outgoing || [],
          incoming_count: connections.incoming_count || 0,
          outgoing_count: connections.outgoing_count || 0,
          total: connections.total || 0
        },

        logoUrl: supplier.photo || '',
        external_id: supplier.external_id || '',
        memberSince: this.formatMemberSince(supplier.created_at),

        company_name: this.ucfirst(supplier.company_name || ''),
        email: supplier.email || '',
        phone: supplier.phone || '',
        physical_address: this.ucfirst(supplier.physical_address || ''),
        street_city: this.ucfirst(supplier.street_city || ''),
        state: this.ucfirst(supplier.state || ''),
        postal_code: supplier.postal_code || '',
        country: supplier.country || '',

        username: supplier.username || '',
        type: supplier.type || '',
        supplier_type: supplier.supplier_type || '',
        registered_through: supplier.registered_through || '',
        risk_assessment: supplier.risk_assessment || '',
        approval_status: supplier.approval_status || '',
        approval_date: supplier.approval_date || '',
        questionnaire_complete_date: supplier.questionnaire_complete_date || '',

        poc: this.ucfirst(supplier.POC || supplier.poc || ''),
        title: this.ucfirst(supplier.title || ''),
        designation: supplier.designation || '',
        after_hour_contact_no: supplier.after_hour_contact_no || '',
        emergency_contact: supplier.emergency_contact || '',
        emergency_contact_name: supplier.emergency_contact_name || '',
        emergency_contact_title: supplier.emergency_contact_title || '',

        qa_name: this.ucfirst(supplier.qa_name || ''),
        qa_title: this.ucfirst(supplier.qa_title || ''),
        qa_email: supplier.qa_email || '',
        qa_phone: supplier.qa_phone || '',
        qa_contact: supplier.qa_contact || '',

        manufacturing_type: supplier.manufacture_type || '',
        manufacturing_site: supplier.manufacture_site || '',
        product_brand_type: supplier.manufacture_category || '',
        production_city: this.ucfirst(supplier.production_city || ''),
        production_state: this.ucfirst(supplier.production_state || ''),
        production_address: this.ucfirst(supplier.production_address || ''),
        production_postcode: supplier.production_postcode || '',

        services: (data.services || []).map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description,
          service_type: s.service_type,
          status: s.status,
        })),

        compliance: (data.questionnaire_responses || []).map((q) => ({
          key: q.question_key || q.id,
          text: q.question_text,
          answer: q.response,
          remarks: q.remarks,
        })),

        nonCert: (data.non_cert_responses || []).map((q) => ({
          key: q.question_key || q.id,
          text: q.question_text,
          answer: q.response,
          details: q.sub_items_response
            ? (() => { try { return JSON.parse(q.sub_items_response) } catch (e) { return q.sub_items_response } })()
            : null,
          remarks: q.remarks,
        })),

        foodSafetyCert: supplier.food_safety_cert || 'no',
        ipCertOption: supplier.ip_certificates || 'na',
        certificates: (data.certificates || []).map((c) => ({
          id: c.id,
          title: this.ucfirst(c.document_name),
          expiryDate: c.expiry_date,
          comment: c.description,
          file: c.document || '',
          fileName: c.document ? c.document.split('/').pop() : '',
        })),
        ipCerts: (data.ip_certificates || []).map((c) => ({
          id: c.id,
          title: this.ucfirst(c.document_name),
          expiryDate: c.expiry_date,
          comment: c.description,
          file: c.document || '',
          fileName: c.document ? c.document.split('/').pop() : '',
        })),

        materials: (data.materials || []).map((m) => ({
          id: m.id,
          name: this.ucfirst(m.name),
          type: this.titleCaseFromSlug(m.type),
          packaging_size: m.pack_size,
          packaging_type: this.titleCaseFromSlug(m.pack_type),
          lead_time: m.lead_time,
          sub_ingredients: m.sub_ingredients || '',
          allergens: (m.get_allergens || []).map((a) => this.ucfirst(a.name)).join(', ') || '—',
          documents: (m.get_attachments || []).map((file) => ({
            name: file.org_name || file.name || 'Document',
            url: file.url || `${file.name}`,
            type: file.type || 'document',
            id: file.id
          }))
        })),
        products: (data.products || []).map((p) => ({
          id: p.id,
          name: this.ucfirst(p.name),
          type: this.titleCaseFromSlug(p.type),
          production_type: this.titleCaseFromSlug(p.production_type),
          unit: p.unit,
          sku: p.sku || '',
          application: this.ucfirst(p.application || ''),
          description: this.ucfirst(p.description || ''),
        })),
        supplier_companies: data.supplier_companies || [],

        comments: this.ucfirst(supplier.comments || ''),
        reviews,
      }
    },
    formatMemberSince(createdAt) {
      if (!createdAt) return ''
      const d = new Date(createdAt)
      if (Number.isNaN(d.getTime())) return createdAt
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    },

    getFileUrl(filePath) {
      return resolveAssetFileUrl(filePath)
    },

    ucfirst(str) {
      if (!str || typeof str !== 'string') return str || ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    titleCaseFromSlug(str) {
      if (!str || typeof str !== 'string') return str || '—'
      return str.split('_').filter(Boolean).map((w) => this.ucfirst(w)).join(' ')
    },
    manufacturerCategoryLabel(value) {
      const map = { private_label: 'Private Label', co_mfg: 'Co Manufacturer', external_brand: 'External Brand' }
      return map[value] || this.titleCaseFromSlug(value)
    },
    typeLabel(type) {
      const map = { supplier: 'Supplier', manufacturer: 'Manufacturer', service_provider: 'Service Provider' }
      return map[type] || this.titleCaseFromSlug(type)
    },
    approvalClass(status) {
      const map = {
        approved: 'asp-status-badge--approved',
        in_progress: 'asp-status-badge--pending',
        pending: 'asp-status-badge--pending',
        rejected: 'asp-status-badge--rejected',
      }
      return map[status] || 'asp-status-badge--neutral'
    },
    riskClass(risk) {
      const map = { low: 'asp-status-badge--approved', medium: 'asp-status-badge--pending', high: 'asp-status-badge--rejected' }
      return map[risk] || 'asp-status-badge--neutral'
    },
    ynClass(answer) {
      if (answer === 'YES') return 'asp-yn--yes'
      if (answer === 'NO') return 'asp-yn--no'
      return 'asp-yn--na'
    },
    ynIcon(answer) {
      if (answer === 'YES') return '✓'
      if (answer === 'NO') return '✕'
      return '—'
    },
  openReviewModal(supplier) {
      console.log(supplier)

      this.reviewingSupplier = supplier
      this.selectedTypeId = supplier.supplier_types?.[0]?.id ?? null
      this.resetDraft()
      this.$refs.reviewModal.showModal()
    },
          resetDraft() {
          this.statusDraft = {
            status: this.selectedType?.approval_status || 'pending',
            remarks: '',
          }
          this.remarksError = ''
        },
          closeReviewModal(refName) {
      this.$refs[refName].closeModal()
      this.reviewingSupplier = null
    },
        async saveTypeStatus() {
          if (!this.selectedType) return

          if (!this.reviewingSupplier?.id) {
            console.error('reviewingSupplier has no id:', this.reviewingSupplier)
            this.remarksError = 'Something went wrong identifying this supplier. Please reopen the review modal.'
            return
          }

          if (this.statusDraft.status !== 'approved' && !this.statusDraft.remarks.trim()) {
            this.remarksError = 'Remarks are required for this status.'
            return
          }
          this.remarksError = ''
          this.saving = true

          const payload = {
            supplier_id: this.reviewingSupplier.id,
            type_id: this.selectedType.id,
            approval_status: this.statusDraft.status,
          }
          if (this.statusDraft.status !== 'approved') {
        payload.remarks = this.statusDraft.remarks
      }
      const result = await form_api_submit({
        url: 'supplier/approve',
        method: 'POST',
        data: payload,
        show_success_toast: true,
        show_error_toast: true,
      })

      this.saving = false

      if (result.success) {
        this.selectedType.approval_status = this.statusDraft.status
        this.resetDraft()
        this.closeReviewModal('reviewModal')
        this.loadProfile()
      }
    },
     formatBusinessType(type) {
      const types = {
        supplier: 'Supplier',
        manufacturer: 'Manufacturer',
        service_provider: 'Service Provider',
      }
      return types[type] || type
    },
      

    getStarDistribution() {
  const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  this.reviews.forEach(r => {
    if (dist[r.stars] !== undefined) dist[r.stars]++
  })
  return dist
},

getInitials(reviewer) {
  if (!reviewer) return '?'
  const name = reviewer.company_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim()
  return name
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
},

getReviewerName(reviewer) {
  if (!reviewer) return 'Anonymous'
  return reviewer.company_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() || 'Anonymous'
},

formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
},
async loadReviews(supplierId) {
  const result = await form_api_submit({
    url: `supplier/${supplierId}/reviews`,
    method: 'GET',
    show_success_toast: false,
    show_error_toast: false
  })

  if (result.success) {
    this.profile.reviews = result.data.reviews || []
  }
},
  toggleConnectionsAccordion() {
    this.showConnections = !this.showConnections;
  },
  getInitialsFromName(companyName) {
    if (!companyName) return '?'
    return companyName
      .split(' ')
      .map(w => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  },
  },
}
</script>
<style scoped>
.asp-topbar__submission {
  margin-top: 6px;
  font-size: 13px;
  color: #ffff;
  display: flex;
  align-items: center;
  gap: 6px;
}

.asp-topbar__submission--pending {
  color: #d97706;
}
/* Connections Accordion - Updated */
.connections-accordion {
  margin-bottom: 24px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--color-surface);
}

.accordion-item {
  width: 100%;
}

.accordion-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--color-surface);
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
  font-family: inherit;
}

.accordion-header:hover {
  background: var(--color-tint-light);
}

.accordion-header__content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.accordion-header__icon {
  font-size: 18px;
}

.accordion-header__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark);
}

.accordion-header__badge {
  background: var(--color-primary);
  color: white;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  min-width: 24px;
  text-align: center;
}

.accordion-header__arrow {
  font-size: 12px;
  transition: transform 0.3s ease;
  color: var(--color-text-muted);
}

.accordion-header__arrow.is-open {
  transform: rotate(180deg);
}

.accordion-body {
  border-top: 1px solid var(--color-border);
  padding: 20px;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stats Grid */
.connections-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--color-tint-light);
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  border: 1px solid var(--color-border);
}

.stat-card__number {
  display: block;
  font-size: 28px;
  font-weight: 800;
  color: var(--color-text-dark);
  line-height: 1.2;
}

.stat-card__label {
  font-size: 12px;
  color: var(--color-text-muted);
  font-weight: 500;
}

.stat-card--total {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.stat-card--total .stat-card__number,
.stat-card--total .stat-card__label {
  color: white;
}

/* Connection List Sections */
.connection-list-section {
  margin-top: 20px;
}

.connection-list-section:first-of-type {
  margin-top: 0;
}

.connection-list-section__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.connection-list-section__badge {
  background: var(--color-tint-light);
  padding: 0 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-muted);
}
/* Connection List - Three columns */
.connection-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

/* For smaller screens, switch to 2 columns */
@media (max-width: 900px) {
  .connection-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* For mobile, switch to 1 column */
@media (max-width: 600px) {
  .connection-list {
    grid-template-columns: 1fr;
  }
}

/* Connection Item - Update for grid layout */
.connection-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  transition: all 0.2s ease;
  min-height: 72px;
}

.connection-item:hover {
  background: var(--color-tint-light);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

/* Avatar styles */
.connection-item__avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: white;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
}

.connection-item__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Info section - takes remaining space */
.connection-item__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0; /* Prevents overflow */
}

.connection-item__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.connection-item__date {
  font-size: 11px;
  color: var(--color-text-muted);
}

/* Status badges */
.connection-item__status {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.connection-item__status--incoming {
  background: rgba(26, 127, 75, 0.1);
  color: #1a7f4b;
}

.connection-item__status--outgoing {
  background: rgba(69, 123, 157, 0.1);
  color: #457b9d;
}

/* Stats Grid - Keep as is but maybe adjust for better spacing */
.connections-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

/* Stat Cards */
.stat-card {
  background: var(--color-tint-light);
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  border: 1px solid var(--color-border);
}

.stat-card__number {
  display: block;
  font-size: 28px;
  font-weight: 800;
  color: var(--color-text-dark);
  line-height: 1.2;
}

.stat-card__label {
  font-size: 12px;
  color: var(--color-text-muted);
  font-weight: 500;
}

.stat-card--total {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.stat-card--total .stat-card__number,
.stat-card--total .stat-card__label {
  color: white;
}

/* Connection List Sections */
.connection-list-section {
  margin-top: 20px;
}

.connection-list-section:first-of-type {
  margin-top: 0;
}

.connection-list-section__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.connection-list-section__badge {
  background: var(--color-tint-light);
  padding: 0 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-muted);
}

/* Empty State */
.connections-empty {
  text-align: center;
  padding: 30px 0;
}

.connections-empty__icon {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

.connections-empty__text {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark);
  margin: 0;
}

.connections-empty__sub {
  font-size: 12px;
  color: var(--color-text-muted);
  margin: 4px 0 0;
}

/* Responsive adjustments */
@media (max-width: 900px) {
  .connections-stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 600px) {
  .connections-stats-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .stat-card--total {
    grid-column: 1 / -1;
  }
}
</style>
